import { inject, observer } from 'mobx-react'
import React, { createRef } from 'react'
import ReactQuill, { Quill } from 'react-quill';
import "react-quill/dist/quill.snow.css";
import { getLetterSpacingList, getLineHeightRange, getOpacityRange } from '../../helper';

const Parchment = Quill.import('parchment');

const Inline = Quill.import('blots/inline')

class SpanBlot extends Inline {

  static create(value){
    const node = super.create(value)
    node.textContent = value
    return node
  }

  static value(node){
    return node
  }

  static formats(node) {
    return node;
  }
}

SpanBlot.blotName = 'spanBlot'
SpanBlot.className = 'text-layer__span'
SpanBlot.tagName = 'span'

Quill.register(SpanBlot, true)

const spacingConfig = {
  scope: Parchment.Scope.INLINE,
  whitelist: getLetterSpacingList()
}

const spacingStyle = new Parchment.Attributor.Style('letter-spacing', 'letter-spacing', spacingConfig)
Quill.register(spacingStyle, true)


const lineHeightConfig = {
  scope: Parchment.Scope.INLINE,
  whitelist: getLineHeightRange()
}

const lhStyle = new Parchment.Attributor.Style('line-height', 'line-height', lineHeightConfig)
Quill.register(lhStyle, true)

const opacityConfig = {
  scope: Parchment.Scope.INLINE,
  whitelist: getOpacityRange()
}

const opacityStyle = new Parchment.Attributor.Style('opacity', 'opacity', opacityConfig)
Quill.register(opacityStyle, true)

@inject('workAreaStore') @observer
class TextLayer extends React.Component {

  constructor(props){
    super(props)
    this.quillRef = createRef()
    this.blotRef = null
    this.state = {
      value: this.props.content,
      changed: true,
      canEdit: false,
      mounted: false,
      registeredFonts: []
    }
    this.formats = [
      "header",
      "font-family",
      'size',
      "bold",
      "italic",
      "underline",
      "strike",
      "blockquote",
      'background',
      "list",
      "bullet",
      "indent",
      "link",
      'line-height',
      'text-shadow',
      "image",
      "color",
      'span',
      'letter-spacing',
      'opacity',
      'align'
    ]
    this.modules = {
        toolbar: {
          container: `#text-toolbar-${btoa(props.id)}`,
          handlers: {
          'increaseSpacing': this.changeSpacing.bind(this, false),
          'increaseSpacingInput': this.changeSpacing.bind(this, true),
          'decreaseSpacingInput': this.changeSpacing.bind(this, true),
          'colorChange': this.changeColor.bind(this),
          'decreaseSpacing': this.changeSpacing.bind(this, false),
          'increaseOpacity': this.changeOpacity.bind(this, false),
          'decreaseOpacity': this.changeOpacity.bind(this, false),
          'decreaseOpacityInput': this.changeOpacity.bind(this, true),
          'increaseOpacityInput': this.changeOpacity.bind(this, true),

          'italic': this.handleItalicChange.bind(this),
          'size': this.handleFontSizeChangeDropdown.bind(this),
          'bold': this.handleBoldChange.bind(this),
          'underline': this.handleUnderlineChange.bind(this),
          'increaseShadowX': this.changeShadowProp.bind(this, 'x', false),
          'increaseShadowY': this.changeShadowProp.bind(this, 'y', false),
          'decreaseShadowX': this.changeShadowProp.bind(this, 'x', false),
          'decreaseShadowY': this.changeShadowProp.bind(this, 'y', false),

          'increaseFontSize': this.handleFontSizeChange.bind(this, false),
          'increaseFontSizeInput': this.handleFontSizeChange.bind(this, true),
          'decreaseFontSize': this.handleFontSizeChange.bind(this, false),
          'decreaseFontSizeInput': this.handleFontSizeChange.bind(this, true),

          'increaseShadowXInput': this.changeShadowProp.bind(this, 'x', true),
          'increaseShadowYInput': this.changeShadowProp.bind(this, 'y', true),
          'decreaseShadowXInput': this.changeShadowProp.bind(this, 'x', true),
          'decreaseShadowYInput': this.changeShadowProp.bind(this, 'y', true),
          'changeShadowColor': this.changeShadowColor.bind(this),
          'toggleShadow': this.toggleShadow.bind(this),

          'increaseLineheight': this.changeLineheight.bind(this, false),
          'decreaseLineheight': this.changeLineheight.bind(this, false),
          'decreaseLineheightInput': this.changeLineheight.bind(this, true),
          'increaseLineheightInput': this.changeLineheight.bind(this, true),
          'changeFont': this.changeFont.bind(this),
        }
      }
    }
  }
  
  componentWillUnmount(){
    const editor = this.quillRef.current.getEditor()
    editor.disable()
    editor.off('selection-chage')
    this.modules = null
    this.formats = null
    this.quillRef = null
  }

  handleFontSizeChangeDropdown(value){
    const editor = this.quillRef.current.getEditor()
    const len = this.state.value.length
    editor.formatText(0, len, 'size', value)
  }

  handleFontSizeChange(isInput = false){
    if(this.quillRef && this.quillRef.current){
      const toolbarHash = `.text-toolbar-${btoa(this.props.id)}`
      let source = document.querySelector(`${toolbarHash} .slider.font-size-slider`)
      if(isInput){
        source = document.querySelector(`${toolbarHash} .font-size-row .prop-display .form-control`)
      }
      const value = Number(source.value) + 'px'
      const editor = this.quillRef.current.getEditor()
      const len = this.state.value.length
      editor.formatText(0, len, 'size', value)
    }
  }

  handleItalicChange(value){
    if(this.quillRef && this.quillRef.current){
      const editor = this.quillRef.current.getEditor()
      const len = this.state.value.length
      editor.formatText(0, len, 'italic', value)
    }
  }

  handleUnderlineChange(value){
    if(this.quillRef && this.quillRef.current){
      const editor = this.quillRef.current.getEditor()
      const len = this.state.value.length
      editor.formatText(0, len, 'underline', value)
    }
  }

  handleBoldChange(value){
    if(this.quillRef && this.quillRef.current){
      const editor = this.quillRef.current.getEditor()
      const len = this.state.value.length
      editor.formatText(0, len, 'bold', value)
    }
  }

  componentDidMount(){
    try{
      const { content: val, 
          workAreaStore, 
          colors, 
          font, 
          italic, 
          underline, 
          textShadow, 
          opacity, 
          lineHeight,
          spacing, 
          weight, 
          textAlign, 
          id, 
          size: fs 
        } = this.props
      if(this.quillRef.current){
        const editor = this.quillRef.current.getEditor()
        editor.focus()
        const parent = editor.editor.scroll.children.tail
        let content = val.trim()
        const newBlot = Parchment.create('spanBlot', String(content))
        this.blotRef = newBlot
        newBlot.insertInto(parent)
        let len = String(content).length
        const { value: stateVal } = this.state
        if(stateVal.length !== len && stateVal.length > 0){
          len = stateVal.length
        }
        editor.formatText(0, len, 'size', fs + 'px')
        editor.on('selection-change', (range, oldRange) => {
          this.handleSelectionChange(range, oldRange)
        })
        workAreaStore.resizeTextLayer(id)
        const end = len
        if(Number(weight) > 400){
          editor.formatText(0, end, 'bold', true)
        }else{
          editor.formatText(0, end, 'bold', false)
        }
        if(opacity !== 1){
          editor.formatText(0, end, 'opacity', opacity)
        }
        if(Number(spacing) > 2){
          const newSpacing = (Number(this.props.spacing) / 1000) + 'em'
          editor.formatText(0, end, 'letter-spacing', newSpacing)
        }
        if(lineHeight){
          const ln = this.props.lineHeight
          editor.formatText(0, end, 'line-height', ln)
        }
        if(textShadow){
          let shadowVal = 'none'
          if(textShadow.display){
            shadowVal = `${textShadow.x}px ${textShadow.y}px ${textShadow.color}`
          }
          const shadowStyle = new Parchment.Attributor.Style('text-shadow', 'text-shadow', {
            scope: Parchment.Scope.INLINE,
            whitelist: shadowVal
          })
          Quill.register(shadowStyle, true)
          editor.formatText(0, end, 'text-shadow', shadowVal)
        }
        if(textAlign){
          editor.formatLine(0, end, 'align', textAlign)
        }
        if(italic){
          editor.formatText(0, end, 'italic', italic)
        }
        if(underline){
          editor.formatText(0, end, 'underline', underline)
        }
        colors.forEach((colorMeta, idx) => {
          if(idx === colors.length - 1){
            this.props.workAreaStore.setLastTextColor(colorMeta.color)
          }
          const { deltaStart, deltaEnd, color } = colorMeta
          if(deltaStart === deltaEnd){
            const split = content.split('')
            if(split[deltaStart] === '\n'){
              return
            }else{
              editor.formatText(deltaStart, deltaEnd + 1, 'color', color)
            }
          }else{
            editor.formatText(deltaStart, deltaEnd + 1, 'color', color)
          }
        })
        if(font && font.id !== 'arial'){
          const fontStyle = new Parchment.Attributor.Style('font-family', 'font-family', {
            scope: Parchment.Scope.INLINE,
            whitelist: font.value
          })
          Quill.register(fontStyle, true)
          editor.formatText(0, end, 'font-family', font.value)
        }
        const selection = editor.getSelection()
        if(selection && !selection.length && workAreaStore.editingTextId === id){
          editor.setSelection(0, len)
        }
        if(workAreaStore.editingTextId === id){
          editor.enable(true)
        }
        if(workAreaStore.editingTextId !== id){
          editor.enable(false)
        }
        editor.clipboard.addMatcher(Node.ELEMENT_NODE, (node, delta) => {
          let ops = []
          const div = document.createElement('div')
          div.appendChild(node)
          const str = div.innerHTML
          if(str.includes('figmeta') && str.includes('data-metadata')){
            return delta
          }
          delta.ops.forEach(op => {
            if (op.insert && typeof op.insert === 'string') {
              ops.push({
                insert: op.insert
              })
            }
          })
          const { lineHeight, size, font, italic, underline, weight, spacing, opacity, textShadow } = this.props
          const attr = {
            'line-height': lineHeight,
            size: size + 'px',
            color: this.props.workAreaStore.lastTextColor,
            opacity: opacity
          }
          if(Number(spacing) > 2){
            const newSpacing = (Number(spacing) / 1000) + 'em'
            attr['letter-spacing'] = newSpacing
          }
          if(textShadow && textShadow.display){
            attr['text-shadow'] = `${textShadow.x}px ${textShadow.y}px ${textShadow.color}`
          }
          if(Number(weight) > 400){
            attr.bold = true
          }else{
            attr.bold = false
          }
          if(font && font.id !== 'arial'){
            attr['font-family'] = font.value
          }
          if(italic){
            attr.italic = true
          }
          if(underline){
            attr.underline = true
          }
          if(ops.length){
            ops[0].attributes = attr
          }else{
            ops.push({
              attributes: attr
            })
          }
          delta.ops = ops
          return delta
        })
        setTimeout(() => {
          this.props.workAreaStore.unsetTextMount()
        }, 50)
      }
    }catch(err){
      console.log(err)
    }
  }

  registerShadow(display, x, y, color){
    const whitelist = ['none']
    if(display){
      whitelist.push(`${x}px ${y}px ${color}`)
    }
    const shadowStyle = new Parchment.Attributor.Style('text-shadow', 'text-shadow', {
      scope: Parchment.Scope.INLINE,
      whitelist: whitelist
    })
    Quill.register(shadowStyle, true)
  }

  toggleShadow(){
    if(this.quillRef && this.quillRef.current){
      const editor = this.quillRef.current.getEditor()
      const { id } = this.props
      const toolbarHash = `.text-toolbar-${btoa(id)}`
      const node = document.querySelector(`${toolbarHash} .shadow-toggle`)
      if(node){
        const value = node.checked
        const { x, y, color } = this.props.textShadow
        this.registerShadow(value, x, y, color)
        const len = this.state.value.length
        if(value){
          const style = this.getTextShadowValue(x, y, color)
          editor.formatText(0, len, 'text-shadow', style)
        }else{
          editor.formatText(0, len, 'text-shadow', 'none')
        }
        this.props.workAreaStore.changeTextLayerProp(this.props.id, `display|textShadow`, value)
      }
    }
  }

  changeShadowColor(){
    if(this.quillRef && this.quillRef.current){
      const editor = this.quillRef.current.getEditor()
      const { id } = this.props
      const toolbarHash = `.text-toolbar-${btoa(id)}`
      const node = document.querySelector(`${toolbarHash} .shadow-color__preview`)
      if(node && this.props.textShadow.display){
        const color = node.style.background
        const { x, y } = this.props.textShadow
        this.registerShadow(true, x, y, color)
        const style = this.getTextShadowValue(x, y, color, true)
        const len = this.state.value.length - 1
        editor.setSelection(len, 0)
        this.props.workAreaStore.changeTextLayerProp(this.props.id, `color|textShadow`, color)
        editor.formatText(0, len, 'text-shadow', style)
      }
    }
  }

  changeShadowProp(axis, isInput = false){
    if(!this.quillRef || !this.quillRef.current){
      return
    }
    const { x, y, color } = this.props.textShadow
    let value = 0
    let xVal = Number(x)
    let yVal = Number(y)
    const toolbarHash = `.text-toolbar-${btoa(this.props.id)}`
    let selector = toolbarHash
    if(isInput){
      if(axis === 'x'){
        selector += ` .text-shadow-x input.form-control`
        const elem = document.querySelector(selector)
        value = Number(elem.value)
        xVal = value
      }else{
        selector += ` .text-shadow-y input.form-control`
        const elem = document.querySelector(selector)
        value = Number(elem.value)
        yVal = value
      }
    }else{
      if(axis === 'x'){
        selector += ` .text-shadow-x-slider`
        const elem = document.querySelector(selector)
        value = Number(elem.value)
        xVal = value
      }else{
        selector += ` .text-shadow-y-slider`
        const elem = document.querySelector(selector)
        value = Number(elem.value)
        yVal = value
      }
    }
    const newVal = [`${xVal}px ${yVal}px ${color}`]
    const editor = this.quillRef.current.getEditor()
    const shadowStyle = new Parchment.Attributor.Style('text-shadow', 'text-shadow', {
      scope: Parchment.Scope.INLINE,
      whitelist: newVal
    })
    Quill.register(shadowStyle, true)
    if(this.props.textShadow.display){
      const len = this.state.value.length
      editor.formatText(0, len, 'text-shadow', newVal[0])
      editor.setSelection(len, 0)
    }
    this.props.workAreaStore.changeTextLayerProp(this.props.id, `${axis}|textShadow`, value)
  }

  handleSizeChange(delta){
    const { id } = this.props
    this.props.workAreaStore.resizeTextLayer(id)
    const newLine = delta && delta.ops ? delta.ops.find(({ insert }) => insert === '\n') : false
    if(newLine){
      setTimeout(() => {
        this.props.workAreaStore.resizeTextLayer(id)
      }, 1)
    }
  }

  handleDelete(delta){
    if(delta.ops){
      const del = delta.ops.find(({ delete: del }) => del)
      const retain = delta.ops.find(({ retain }) => retain)
      if(del && retain){
        const editor = this.quillRef.current.getEditor()
        const [leaf, index] = editor.getLeaf(retain.retain)
        if(index === 0 && leaf && leaf.domNode.nodeName === 'BR'){
          let prevIdx = retain.retain - 1
          let [prevLeaf] = editor.getLeaf(prevIdx)
          let looped = false
          while(prevLeaf && prevLeaf.domNode && prevLeaf.domNode.nodeName === 'BR' && prevIdx > 0){
            editor.deleteText(prevIdx, 1, 'user')
            prevIdx -= 1
            let prev = editor.getLeaf(prevIdx)
            prevLeaf = prev[0]
            looped = true
          }
          if(!looped){
            editor.deleteText(retain.retain, 1, 'user')
          }
        }
        if(index > 0 && leaf){
          const format = editor.getFormat(retain.retain)
          if(format && format.color && this.props.workAreaStore.lastTextColor !== format.color){
            this.props.workAreaStore.setLastTextColor(format.color)
          }
        }
      }
    }
  }

  handleEnter(range, context){
    const editor = this.quillRef.current.getEditor()
    if (range.length > 0) {
      editor.scroll.deleteAt(range.index, range.length); // So we do not trigger text-change
    }
    const format = context.format
    delete format.spanBlot
    const leaf = editor.getLeaf(range.index)[0]
    editor.insertText(range.index, "\n");
  }  

  componentDidUpdate(prevProps){
    try{
      const prev = this.props.workAreaStore.editingText
      const prevSize = this.props.size
      const resizingLayer = this.props.workAreaStore
      if(!this.quillRef.current){
        return
      }
      const editor = this.quillRef.current.getEditor()
      const layers = this.props.workAreaStore.getAllLayers()
      const idx = layers.findIndex(({ id }) => id === this.props.id)
      const families = this.props.workAreaStore.loadedFonts.map(font => font.family)
      const fontStyle = new Parchment.Attributor.Style('font-family', 'font-family', {
        scope: Parchment.Scope.INLINE,
        whitelist: families
      })
      Quill.register(fontStyle, true)
      if(idx === -1){
        return
      }
      if(prev && !this.props.workAreaStore.editingTextId && this.state.value.trim() !== ''){
        this.props.workAreaStore.handleTextLayerChange(this.props.id, this.state.value)
      }
      const end = this.state.value.length
      const newSize = Math.floor(this.props.size)
      if(prevProps.textAlign !== this.props.textAlign && editor){
        editor.formatLine(0, end, 'align', this.props.textAlign)
      }
      if(prevProps.colors !== this.props.colors){
        this.props.colors.forEach(colorMeta => {
          const { deltaStart, deltaEnd, color } = colorMeta
          editor.formatText(deltaStart, deltaEnd + 1, 'color', color)
        })
      }
      if(this.props.textShadow){
        if(this.props.textShadow.display){
          const { x, y, color } = this.props.textShadow
          this.registerShadow(true, x, y, color)
          const shadowCSS = this.getTextShadowValue(x, y, color, true)
          editor.formatText(0, end, 'text-shadow', shadowCSS)
        }else{
          this.registerShadow(false)
          editor.formatText(0, end, 'text-shadow', 'none')
        }
      }
      if(prevSize !== newSize && resizingLayer && editor){
        editor.formatText(0, end, 'size', newSize + 'px')
      }
      const newSpacing = (Number(this.props.spacing) / 1000) + 'em'
      editor.formatText(0, end, 'letter-spacing', newSpacing)
      const val = this.props.lineHeight
      editor.formatText(0, end, 'line-height', val)
      editor.formatText(0, end, 'opacity', this.props.opacity)
      if((!prevProps.font && this.props.font && editor) || (prevProps.font && prevProps.font.id !== this.props.font.id && editor)) {
        editor.formatText(0, end, 'font-family', this.props.font.value)
      }
    }catch(err){
      console.log(err)
    }
  }

  changeColor(){
    if(this.quillRef && this.quillRef.current){
      const { id } = this.props
      const toolbarHash = `.text-toolbar-${btoa(id)}`
      const node = document.querySelector(`${toolbarHash} select.ql-colorChange option[selected="selected"]`)
      const colorValue = node.value
      const editor = this.quillRef.current.getEditor()
      const selection = editor.getSelection()
      if(selection && selection.length){
        editor.formatText(selection.index, selection.length, 'color', colorValue)
      }else{
        editor.format('color', colorValue)
      }
      this.props.workAreaStore.setLastTextColor(colorValue)
      const input = document.querySelector(`${toolbarHash} .react-colorful .text-color-input`)
      if(input){
        input.focus()
      }
    }
  }

  registerAndFormatFont(len, editor){
    const fontStyle = new Parchment.Attributor.Style('font-family', 'font-family', {
      scope: Parchment.Scope.INLINE,
      whitelist: this.props.font.value
    })
    Quill.register(fontStyle, true)
    editor.formatText(0, len, 'font-family', this.props.font.value)
  }

  getTextShadowValue(x, y, color, enabled = true){
    if(!enabled){
      return 'none'
    }
    return `${x}px ${y}px ${color}`
  }

  handleQuillChange(delta){
    try{
      if(!this.quillRef){
        return
      }
      if(!this.quillRef.current){
        return
      }
      const editor = this.quillRef.current.getEditor()
      const unpriv = this.quillRef.current.makeUnprivilegedEditor(editor)
      const newVal = String(unpriv.getText())
      const { workAreaStore, id: layerId } = this.props
      this.setState({ value: newVal })
      if(delta.ops){
        if(workAreaStore.resizingLayer){
          return
        }
        if(!this.quillRef || !this.quillRef.current){
          return
        }
        delta.ops.forEach((op, idx) => {
          const len = newVal.length
          if(op.attributes){
            if(op.attributes.hasOwnProperty('align') && op.attributes.align === null){
              editor.formatLine(0, len, 'align', 'left')
              workAreaStore.changeTextLayerProp(layerId, 'textAlign', 'left')
            }
            if(op.attributes.align && op.attributes.align !== this.props.textAlign){
              const val = op.attributes.align
              editor.formatLine(0, len, 'align', val)
              workAreaStore.changeTextLayerProp(layerId, 'textAlign', val)
            }
            if(op.attributes.size){
              const newSize = op.attributes.size
              editor.formatText(0, len, 'size', newSize)
              const sizeNum = Number(newSize.replace(/px/ig, ''))
              workAreaStore.setTextLayerSize(sizeNum, layerId)
            }
            if(op.attributes.hasOwnProperty('bold')){
              const bold = op.attributes.bold
              const boldVal = bold ? bold : false
              workAreaStore.changeTextWeight(layerId, boldVal)
              editor.formatText(0, len, 'bold', boldVal)
            }
            if(op.attributes.hasOwnProperty('italic')){
              const val = op.attributes.italic ? true : false
              workAreaStore.changeTextLayerProp(layerId, 'italic', val)
              editor.formatText(0, len, 'italic', val)
            }
            if(op.attributes.hasOwnProperty('underline')){
              const val = op.attributes.underline ? true : false
              workAreaStore.changeTextLayerProp(layerId, 'underline', val)
              editor.formatText(0, len, 'underline', val)
            }
            this.registerAndFormatFont(len, editor)
          }
        })
      }
      if(!this.quillRef || !this.quillRef.current){
        return
      }
      const len = newVal.trim().length
      if(len === 0){
        editor.format('color', this.props.workAreaStore.lastTextColor)
        const newSpacing = (this.props.spacing / 1000) + 'em'
        const ln = this.props.lineHeight
        editor.format('letter-spacing', newSpacing)
        editor.format('line-height', ln)
        editor.format('opacity', this.props.opacity)
        const newSize = Math.floor(this.props.size)
        editor.format('size', newSize + 'px')
        if(Number(this.props.weight) > 400){
          editor.format('bold', true)
        }else{
          editor.format('bold', false)
        }
        editor.format('italic', this.props.italic)
        editor.format('underline', this.props.underline)
        this.registerAndFormatFont(1, editor)
        editor.focus()
      }
      workAreaStore.handleTextLayerChange(this.props.id, newVal)
      this.handleSizeChange(delta)
      this.handleDelete(delta)
    }catch(err){
      console.log(delta)
      console.log(err)
    }
  }

  changeSpacing(isInput = false){
    if(this.quillRef && this.quillRef.current){
      const { id, spacing, workAreaStore } = this.props
      let classname = '.text-spacing-slider'
      const base = `.text-toolbar-${btoa(id)}`
      if(isInput){
        classname = '.spacing-row .prop-control input'
      }
      const node = document.querySelector(`${base} ${classname}`)
      const value = Number(node.value)
      if(value === spacing){
        return
      }
      const larger = value > spacing
      if(larger){
        workAreaStore.changeTextLayerProp(id, 'spacing', value)
      }else{
        workAreaStore.changeTextLayerProp(id, 'spacing', value)
      }
    }
  }

  handleSelectionChange(range, oldRange){
    if(!this.quillRef || !this.quillRef.current){
      return
    }
    const editor = this.quillRef.current.getEditor()
    if(!range || !range.index){
      return
    }
    const format = editor.getFormat(range.index, range.length)
    if(format && format.color && this.props.workAreaStore.lastTextColor !== format.color){
      this.props.workAreaStore.setLastTextColor(format.color)
    }
  }

  changeFont(){
    if(this.quillRef && this.quillRef.current){
      const { id, font, workAreaStore } = this.props
      const node = document.querySelector(`.text-toolbar-${btoa(id)} .font-change-value`)
      const idNode = document.querySelector(`.text-toolbar-${btoa(id)} .font-change-id`)
      if(font && font.id === idNode.value){
        return
      }
      const fontObj = {
        id: idNode.value,
        value: node.value
      }
      workAreaStore.changeTextLayerProp(id, 'font', fontObj)
    }
  }

  changeLineheight(isInput = false){
    if(this.quillRef && this.quillRef.current){
      const { id, lineHeight, workAreaStore } = this.props
      let classname = '.text-lineheight-slider'
      if(isInput){
        classname = '.lineheight-row .prop-control input'
      }
      const node = document.querySelector(`.text-toolbar-${btoa(id)} ${classname}`)
      const value = Number(node.value)
      if(value === lineHeight){
        return
      }
      const larger = value > lineHeight
      if(larger){
        workAreaStore.changeTextLayerProp(id, 'lineHeight', value / 100)
      }else{
        workAreaStore.changeTextLayerProp(id, 'lineHeight', value / 100)
      }
    }
  }

  changeOpacity(isInput = false){
    if(this.quillRef && this.quillRef.current){
      const { id, spacing, workAreaStore } = this.props
      let classname = '.text-opacity-slider'
      if(isInput){
        classname = '.opacity-row .prop-control input'
      }
      const node = document.querySelector(`.text-toolbar-${btoa(id)} ${classname}`)
      const value = Number(node.value)
      if(value === spacing){
        return
      }
      const larger = value > spacing
      if(larger){
        workAreaStore.changeTextLayerProp(id, 'opacity', value / 100)
      }else{
        workAreaStore.changeTextLayerProp(id, 'opacity', value / 100)
      }
    }
  }

  render(){
    const { id, canSelect } = this.props
    const className = `workarea-tools__text-layer ${btoa(id)}${canSelect ? '' : ' no-select'}`
    return (
      <div 
        className={className} 
        key={id}
      >
        <ReactQuill
          key={id}
          modules={{
            ...this.modules
          }}
          theme='snow'
          formats={this.formats}
          onSelectionChange={selection => this.handleSelectionChange(selection)}
          onChange={(c, delta) => this.handleQuillChange(delta)}
          ref={this.quillRef}
        />
      </div>
    )
  }

}

export default TextLayer